import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { ServiceCard } from './service-card'
import { ErrorOutline } from '@mui/icons-material'
import { useReadPaymentRecords } from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { useMemo } from 'react'
import { AdditionalService } from '@/types'

export function ServicesList() {
  const { company } = useCompany()

  const { data, error, isLoading } = useReadPaymentRecords({
    status: 'accepted',
    deal_key: company.deal.key,
  })

  const { data: records = [] } = data ?? {}

  // Group records by payment_plan_id and show only one record per payment plan
  const uniqueRecords = useMemo(() => {
    // Use reduce to process all records in a single pass
    const recordMap = records.reduce((result, currentRecord) => {
      const { payment_plan_id, id, invoice } = currentRecord

      const record = {
        ...currentRecord,
        cashflow_date: `${invoice.year}-${invoice.month}-01`,
      }

      // Handle records with payment_plan_id
      if (payment_plan_id) {
        // Check if we already have a record with this payment_plan_id
        const existingRecord = result.get(payment_plan_id)

        if (existingRecord) {
          result.set(payment_plan_id, {
            ...existingRecord,
            cashflow_date: `${existingRecord.invoice.year}-${existingRecord.invoice.month}-01`,
            // clean description by removing installment patterns like "(1/2)"
            description: `${existingRecord.description.replace(/\s*\(\d+\/\d+\)\s*/, '').trim()}`,
          })
        } else {
          // This is the first record with this payment_plan_id
          result.set(payment_plan_id, record)
        }
      } else {
        // Records without payment_plan_id are always included
        result.set(id, record)
      }

      return result
    }, new Map<number, AdditionalService>())

    return Array.from(recordMap.values())
  }, [records])

  const recordsWithoutPlan = useMemo(() => {
    return uniqueRecords.filter((record) => record.type !== 'subscription')
  }, [uniqueRecords])

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} variant="rounded" height={100} />
        ))}
      </Stack>
    )
  }

  if (error) {
    return (
      <Box textAlign="center">
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar os serviços adicionais.
        </Typography>
      </Box>
    )
  }

  if (recordsWithoutPlan.length === 0) {
    return (
      <Typography color="gray" textAlign="center" py={2}>
        Sem serviços adicionais
      </Typography>
    )
  }

  return (
    <Stack spacing={2}>
      {recordsWithoutPlan.map((record) => (
        <ServiceCard key={record.id} data={record} />
      ))}
    </Stack>
  )
}
